// frontend/src/components/datahealth/CellRenderers.jsx
//
// Custom AG Grid cell renderers for Data Health detail grids.
// Only instant-action renderers (toggle, activate) and navigation links.
// Batch-editable columns use AG Grid's native editing (editable: true)
// with the standard onCellValueChanged + changedRows pattern.

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Tooltip,
} from '@mui/material';
import { trackEvent } from '../../hooks/useTracking';
import { dosTargetFixLabelKey } from '../../datahealth/dosTargetFix';



// ============================================
// ToggleFixButtonCellRenderer — for the boolean-setting issues
// (tracking_off / overselling / not_purchasable / digital_item)
// ============================================
//
// Each of these issues is resolved by flipping ONE boolean on the item /
// item-location to a fixed value (ISSUE_DEFS `fixedValue`). It is a one-shot
// fix, not a reversible on/off state — so it renders as a labeled action button
// (consistent with Activate / Remove and readable at a glance) rather than a
// bare Switch, which reads as a cryptic, unlabeled control in a headerless
// column. The per-issue verb comes from `cellRendererParams.field` (the
// issueKey) via `dataHealth.<issueKey>Action`. Calls `context.onAction(rowId)`;
// the page builds the bulk-update payload from the def.

export function ToggleFixButtonCellRenderer({ data, context, colDef }) {
  const { t } = useTranslation();
  const { onAction } = context;
  const issueKey = colDef.cellRendererParams?.field;
  const [saving, setSaving] = useState(false);

  const handleFix = useCallback(async () => {
    if (saving || !data) return;
    setSaving(true);
    try {
      await onAction(data.id);
    } finally {
      setSaving(false);
    }
  }, [data, onAction, saving]);

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="contained" onClick={handleFix} loading={saving}>
        {t(`dataHealth.${issueKey}Action`)}
      </Button>
    </Box>
  );
}

// ============================================
// ActivateButtonCellRenderer — for inactive_with_stock (instant action)
// ============================================

export function ActivateButtonCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onAction } = context;
  const [saving, setSaving] = useState(false);

  const handleActivate = useCallback(async () => {
    if (saving || !data) return;
    setSaving(true);
    try {
      await onAction(data.id);
    } finally {
      setSaving(false);
    }
  }, [data, onAction, saving]);

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="contained" onClick={handleActivate} loading={saving}>
        {t('dataHealth.activate')}
      </Button>
    </Box>
  );
}

// ============================================
// RemoveButtonCellRenderer — for stale_placement (instant action, DELETE)
// ============================================
//
// Calls `context.onDelete(rowId)` — the page-level handler DELETEs the
// ItemLocation (guarded server-side; a 409 surfaces a "still in use" message).

export function RemoveButtonCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onDelete } = context;
  const [saving, setSaving] = useState(false);

  const handleRemove = useCallback(async () => {
    if (saving || !data) return;
    setSaving(true);
    try {
      await onDelete(data.id);
    } finally {
      setSaving(false);
    }
  }, [data, onDelete, saving]);

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="outlined" color="error" onClick={handleRemove} loading={saving}>
        {t('dataHealth.remove')}
      </Button>
    </Box>
  );
}

// ============================================
// CreateInChannelButtonCellRenderer — for erp_item_missing_channel_product
// ============================================
//
// Calls `context.onCreateInChannel(rowId)` which posts to the ItemViewSet
// create-in-channel action AND writes the result back onto the row
// (`channel_created` / `channel_admin_url`). Reading the "done" state from the
// ROW rather than local component state is deliberate: AG Grid recycles cell
// renderers on scroll, so local `useState` would lose the link when the cell
// scrolls out and back. On success the button becomes a deep link to the new
// product so the merchant can open it and add images etc.

export function CreateInChannelButtonCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onCreateInChannel, channel } = context;
  const [saving, setSaving] = useState(false);

  const handleCreate = useCallback(async () => {
    if (saving || !data) return;
    setSaving(true);
    try {
      await onCreateInChannel(data.id);
    } finally {
      setSaving(false);
    }
  }, [data, onCreateInChannel, saving]);

  if (!data) return null;

  if (data.channel_created) {
    // The product is created with the item's status (active items → an active
    // Shopify product) but is NOT published to any sales channel and has no
    // images. Link the merchant to it to finish setup (add images, publish),
    // rather than implying it's already live to customers.
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
        {data.channel_admin_url ? (
          <Tooltip title={t('dataHealth.channelProductSetupHint', { channel })}>
            <Button
              className="grid-action-btn"
              variant="text"
              href={data.channel_admin_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('item_finish_setup_opened_in_channel')}
            >
              {t('dataHealth.finishSetup')}
            </Button>
          </Tooltip>
        ) : (
          <span style={{ color: 'var(--color-text-muted)' }}>{t('dataHealth.created')}</span>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="contained" onClick={handleCreate} loading={saving}>
        {t('dataHealth.createInChannel', { channel })}
      </Button>
    </Box>
  );
}

// ============================================
// LinkSupplierButtonCellRenderer — for supplier_not_linked_to_erp
// ============================================
//
// Calls `context.onLinkSupplier(rowData)` to open LinkSupplierToExactDialog for
// this supplier. Linking is a bespoke connector action (merge/adopt against the
// ERP), NOT a bulk-update — so unlike the toggle/activate renderers it hands the
// whole row up (the dialog needs supplier_id + supplier_name), and the page owns
// the dialog + success handling.

export function LinkSupplierButtonCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onLinkSupplier } = context;

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="contained" onClick={() => onLinkSupplier(data)}>
        {t('dataHealth.linkToErp')}
      </Button>
    </Box>
  );
}

// ============================================
// FixOrderRulesButtonCellRenderer — for contradictory_order_constraints
// ============================================
//
// Deep-link resolution: the three interacting order fields (MOQ / max OQ /
// order multiple) are edited together on the Supplier catalog screen, so this
// hands the merchant there for THIS item via `context.onFixOrderRules(rowData)`
// rather than editing inline.

export function FixOrderRulesButtonCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onFixOrderRules } = context;

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="contained" onClick={() => onFixOrderRules(data)}>
        {t('dataHealth.fixOrderRules')}
      </Button>
    </Box>
  );
}

// ============================================
// FixDosTargetCellRenderer — for dos_target_below_cycle
// ============================================
//
// Deep-link resolution: the row's `fix_level` names where the short Days of
// supply target is fixed — the item, its default supplier or the store — and
// the button says which (datahealth/dosTargetFix.js) and goes there via
// `context.onFixDosTarget(rowData)`.

export function FixDosTargetCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onFixDosTarget } = context;

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button className="grid-action-btn" variant="contained" onClick={() => onFixDosTarget(data)}>
        {t(dosTargetFixLabelKey(data.fix_level))}
      </Button>
    </Box>
  );
}

// ============================================
// ErpLeftoverActionsCellRenderer — for erp_leftover_item / erp_leftover_location
// ============================================
//
// Two per-row choices for an ERP-detached leftover row:
//   Keep   → context.onKeepLeftover(rowData)  — adopt as a permanent native
//            record (clears the erp_detached origin).
//   Retire → context.onRetireLeftover(rowData) — soft-delete; the handler warns
//            (naming attached stock) before committing.

export function ErpLeftoverActionsCellRenderer({ data, context }) {
  const { t } = useTranslation();
  const { onKeepLeftover, onRetireLeftover } = context;
  // Which choice is running: its own button shows the spinner, both wait.
  const [running, setRunning] = useState(null);

  const run = useCallback(async (choice, fn) => {
    if (running || !data) return;
    setRunning(choice);
    try {
      await fn(data);
    } finally {
      setRunning(null);
    }
  }, [running, data]);

  if (!data) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }} onMouseDown={e => e.stopPropagation()}>
      <Button
        className="grid-action-btn"
        variant="outlined"
        onClick={() => run('keep', onKeepLeftover)}
        loading={running === 'keep'}
        disabled={running !== null}
      >
        {t('dataHealth.keep')}
      </Button>
      <Button
        className="grid-action-btn"
        variant="outlined"
        color="error"
        onClick={() => run('retire', onRetireLeftover)}
        loading={running === 'retire'}
        disabled={running !== null}
      >
        {t('dataHealth.retire')}
      </Button>
    </Box>
  );
}
